import React from "react";

interface HalfDoughnutChartProps {
  /** Score value (0-100 for percentage, or actual value if using scale) */
  value: number;
  /** Maximum value (100 for percentage, or scaleMax if using scale) */
  maxValue: number;
  /** Hex color code (with #) */
  color: string;
  /** Text to display in the center */
  displayText: string;
  /** Optional label under the score */
  label?: string;
}

const HalfDoughnutChart: React.FC<HalfDoughnutChartProps> = ({
  value,
  maxValue,
  color,
  displayText,
  label,
}) => {
  // Calculate percentage for the arc (0-100)
  const percentage = Math.min(100, Math.max(0, (value / maxValue) * 100));

  // SVG configuration - consistent sizing
  const size = 160;
  const strokeWidth = 16;
  const center = size / 2;
  const radius = (size - strokeWidth) / 2;

  // Calculate the circumference
  const circumference = Math.PI * radius;

  // Calculate the stroke-dasharray for the percentage
  const progress = (percentage / 100) * circumference;

  return (
    <div className="w-full flex flex-col items-center justify-center px-4">
      <svg
        viewBox={`0 0 ${size} ${size / 2 + 30}`}
        className="w-full max-w-[180px]"
        style={{ height: "auto" }}
      >
        {/* Background half circle (gray) */}
        <path
          d={`M ${strokeWidth / 2},${center} A ${radius},${radius} 0 0 1 ${
            size - strokeWidth / 2
          },${center}`}
          fill="none"
          stroke="#E5E7EB"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />

        {/* Filled half circle (colored) */}
        <path
          d={`M ${strokeWidth / 2},${center} A ${radius},${radius} 0 0 1 ${
            size - strokeWidth / 2
          },${center}`}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={`${progress} ${circumference}`}
          style={{
            transition: "stroke-dasharray 0.3s ease",
          }}
        />

        {/* Center text - Score */}
        <text
          x={center}
          y={label ? center - 18 : center - 12}
          textAnchor="middle"
          dominantBaseline="middle"
          style={{
            fontSize: "28px",
            fontWeight: "bold",
            fill: "#111827",
          }}
        >
          {displayText.includes("%") ? (
            <>
              <tspan>{displayText.replace("%", "")}</tspan>
              <tspan
                style={{
                  fontSize: "18px",
                  fontWeight: "normal",
                  fill: "#6B7280",
                }}
              >
                %
              </tspan>
            </>
          ) : (
            displayText
          )}
        </text>

        {/* Center text - Label */}
        {label && (
          <text
            x={center}
            y={center + 6}
            textAnchor="middle"
            dominantBaseline="middle"
            style={{
              fontSize: "10px",
              fill: "#6B7280",
            }}
          >
            {label}
          </text>
        )}
      </svg>
    </div>
  );
};

export default HalfDoughnutChart;
